import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProgressSteps from "../components/ProgressSteps";
import FieldPickerStep from "../components/FieldPickerStep";
import FieldFilteredSkillsStep from "../components/FieldFilteredSkillsStep";
import ExperienceStep from "../components/ExperienceStep";
import { getFields, postRecommend } from "../lib/api";
import { toast } from "sonner";

const STEPS = ["Your field", "Your skills", "Experience"];

const Assessment = () => {
  const navigate = useNavigate();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  const [primaryFieldId, setPrimaryFieldId] = useState(null);
  const [secondaryFieldId, setSecondaryFieldId] = useState(null);

  const [selectedTechnical, setSelectedTechnical] = useState([]);
  const [selectedSoft, setSelectedSoft] = useState([]);
  const [totalYears, setTotalYears] = useState(0);
  const [localBand, setLocalBand] = useState("none");

  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const f = await getFields();
        setFields(f.items || []);
      } catch (e) {
        toast.error("Couldn't load the assessment. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (list, setter) => (name) =>
    setter(list.includes(name) ? list.filter((s) => s !== name) : [...list, name]);

  const canContinue = useMemo(() => {
    if (stepIdx === 0) return Boolean(primaryFieldId);
    if (stepIdx === 1) return (selectedTechnical.length + selectedSoft.length) >= 3;
    return totalYears >= 0 && Boolean(localBand);
  }, [stepIdx, primaryFieldId, selectedTechnical, selectedSoft, totalYears, localBand]);

  const handleNext = async () => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        technical_skills: selectedTechnical,
        soft_skills: selectedSoft,
        total_years_experience: totalYears,
        local_band: localBand,
        // Phase 2 metadata — not used by the engine, kept for analytics.
        primary_field_id: primaryFieldId,
        secondary_field_id: secondaryFieldId,
      };
      const data = await postRecommend(payload);
      sessionStorage.setItem("rolefinder_result", JSON.stringify(data));
      sessionStorage.setItem("rolefinder_payload", JSON.stringify(payload));
      sessionStorage.removeItem("rolefinder_unlocked");
      navigate("/unlock");
    } catch (e) {
      toast.error("Something went wrong while scoring. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (stepIdx === 0) {
      navigate("/");
      return;
    }
    setStepIdx(stepIdx - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="flex items-center justify-between gap-4 mb-8">
          <ProgressSteps steps={STEPS} current={stepIdx} />
          <span
            data-testid="step-counter"
            className="text-sm text-slate-500 hidden sm:inline"
          >
            Step {stepIdx + 1} of {STEPS.length}
          </span>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 lg:p-10">
          {loading ? (
            <div className="h-48 flex items-center justify-center text-slate-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading…
            </div>
          ) : (
            <div
              key={stepIdx}
              className="animate-fade-up"
              data-testid={`assessment-step-${stepIdx}`}
            >
              {stepIdx === 0 && (
                <FieldPickerStep
                  fields={fields}
                  primaryFieldId={primaryFieldId}
                  secondaryFieldId={secondaryFieldId}
                  onPrimaryChange={setPrimaryFieldId}
                  onSecondaryChange={setSecondaryFieldId}
                />
              )}
              {stepIdx === 1 && (
                <FieldFilteredSkillsStep
                  fields={fields}
                  primaryFieldId={primaryFieldId}
                  secondaryFieldId={secondaryFieldId}
                  selectedTechnical={selectedTechnical}
                  selectedSoft={selectedSoft}
                  onToggleTechnical={toggle(selectedTechnical, setSelectedTechnical)}
                  onToggleSoft={toggle(selectedSoft, setSelectedSoft)}
                />
              )}
              {stepIdx === 2 && (
                <ExperienceStep
                  totalYears={totalYears}
                  onTotalYears={setTotalYears}
                  localBand={localBand}
                  onLocalBand={setLocalBand}
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            data-testid="back-btn"
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="flex items-center gap-3">
            {stepIdx === 0 && !primaryFieldId && (
              <span className="text-xs text-slate-500 hidden sm:inline">
                Pick at least one field to continue
              </span>
            )}
            {stepIdx === 1 && (selectedTechnical.length + selectedSoft.length) < 3 && (
              <span className="text-xs text-slate-500 hidden sm:inline">
                Select at least 3 skills total
              </span>
            )}
            <button
              type="button"
              data-testid={stepIdx === STEPS.length - 1 ? "submit-btn" : "next-btn"}
              onClick={handleNext}
              disabled={!canContinue || submitting}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Scoring…
                </>
              ) : stepIdx === STEPS.length - 1 ? (
                <>
                  Get my recommendation <ArrowRight size={16} />
                </>
              ) : (
                <>
                  Continue <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Assessment;
